//
// detect.ts — finding Mapbox Map instances inside the customer's page.
//
// We try three strategies, in order of how much of the customer's
// environment we need to cooperate:
//
//   1. Monkey-patch `window.mapboxgl`. Works for UMD/CDN builds that expose
//      the library globally. Patches the Map constructor so every future
//      `new mapboxgl.Map(...)` is captured and forced to use
//      preserveDrawingBuffer: true. Handles both "already loaded" and
//      "loaded after our script" via an Object.defineProperty accessor.
//
//   2. MutationObserver + React fiber walk. For bundled apps (webpack,
//      Vite, Parcel, Next, ...) that import mapbox-gl as an ES module and
//      never write the library to `window`. Whenever a `.mapboxgl-map`
//      element appears, we read its React internals to find the Map
//      instance that the component holds as a ref, hook value, or context.
//
//   3. The explicit window.__mve_registerMap(map) API. Silent fallback
//      for non-React apps that also don't use UMD. The customer calls it
//      once after constructing their map.
//
// Strategies (1) and (2) are zero-touch — the customer only needs the
// <script> tag. (3) is the bail-out for the long tail.
//
import type { Map as MapboxMap } from 'mapbox-gl';

//
// Callback fired once per newly-detected Map instance. See onInstance().
//
type Listener = (map: MapboxMap) => void;

//
// Detected Map instances and everything listening for new ones. Stored
// in module scope so all three detection strategies feed the same set.
//
const instances = new Set<MapboxMap>();
const listeners = new Set<Listener>();

//
// Containers we've already walked for React fibers. We re-walk if we
// haven't found any Map yet (the fiber may not have wired up the ref
// when we first looked), but stop once we've found something for this
// element, to keep MutationObserver/interval scans cheap.
//
const fiberWalkedContainers = new WeakSet<HTMLElement>();

//
// Add a Mapbox Map instance to our set of known instances and notify
// every registered listener. Deduplicated on identity so repeated
// detection from different strategies is harmless.
//
// Callers may pass a react-map-gl MapRef (or any wrapper that holds
// the real Map behind a getMap() accessor). We unwrap to the true
// Mapbox Map instance before registering — otherwise code that
// relies on Mapbox-only APIs (setLayoutProperty, addLayer, etc.)
// silently no-ops against the wrapper.
//
//
// Names for the three detection strategies, used purely for the
// "registered map" log line so we know at a glance which path
// caught a given instance.
//
export type DetectionStrategy =
  | 'React fiber walk'
  | 'mapboxgl monkey-patch'
  | '__mve_registerMap API';

export function registerInstance(
  candidate: unknown,
  strategy: DetectionStrategy = 'React fiber walk',
): void {
  const map = tryResolveMap(candidate);
  if (!map) return;
  if (instances.has(map)) return;
  instances.add(map);
  const name = (map as { constructor?: { name?: string } }).constructor?.name ?? 'unknown';
  console.log(`[mve] registered map (${name}) via ${strategy}`);
  for (const fn of listeners) fn(map);
}

//
// Subscribe to Map instance discoveries. The callback fires immediately
// for each map already known, then once per future map. Returns an
// unsubscribe function.
//
export function onInstance(fn: Listener): () => void {
  for (const m of instances) fn(m);
  listeners.add(fn);
  return () => listeners.delete(fn);
}

//
// Return a snapshot of all detected Map instances. A copy so callers
// can iterate without worrying about concurrent mutation.
//
export function getInstances(): MapboxMap[] {
  return [...instances];
}

//
// Entry point called from main.ts once activation via ?mapvideo=1 is
// confirmed. Installs every detection strategy. Never called on
// normal (non-recorder) page loads, so none of these patches or
// observers touch a customer's production traffic.
//
export function installDetection(): void {
  // The __mve_registerMap / __mve_instances globals. A manual opt-in
  // path for apps where neither of the automatic strategies fires,
  // and a handy handle from the browser console during development.
  exposeRegistrationApi();
  // Patch window.mapboxgl for CDN/UMD customers (either now or when
  // the property is assigned by a later script). No-op for
  // webpack/ESM apps that never write mapboxgl to window.
  tryPatchGlobalMapboxgl();
  // Force preserveDrawingBuffer on every future WebGL context so the
  // capture pipeline can read pixels. Catches bundled apps that the
  // mapboxgl patch misses.
  patchGetContextForCapture();
  // Watch the DOM for .mapboxgl-map elements and walk their React
  // fibers to find the Map instance. The dominant strategy for
  // modern bundled apps.
  watchForMaps();
}

//
// Expose window.__mve_registerMap and window.__mve_instances so that
// (a) customers who don't get caught by any automatic strategy can
// opt in manually with one line, and (b) we have a handy debug handle
// from the browser console.
//
function exposeRegistrationApi(): void {
  const w = window as unknown as {
    __mve_registerMap?: (map: MapboxMap) => void;
    __mve_instances?: () => MapboxMap[];
  };
  w.__mve_registerMap = (map) => registerInstance(map, '__mve_registerMap API');
  w.__mve_instances = () => getInstances();
}

//
// Shape of the library object the customer (or their CDN script)
// assigns to `window.mapboxgl`. We only need the Map constructor from
// it — everything else is opaque to us.
//
interface GlobalMapboxgl {
  Map: new (...args: unknown[]) => MapboxMap;
}

//
// Intercept `window.mapboxgl` so we can wrap its Map constructor.
//
// Two cases are handled:
//
//   A. mapboxgl is already present when we run — patch it immediately.
//      Happens when the customer loads the UMD build via <script> tags
//      placed *before* our embed.
//
//   B. mapboxgl will be assigned later — install an Object.defineProperty
//      accessor on window.mapboxgl that fires the moment some later
//      script does `window.mapboxgl = ...`. We pick up that object and
//      patch its Map constructor synchronously inside the setter.
//
// Requires our embed script to run before any Map is constructed. That's
// why the integration guide says to put the <script> tag as the first
// thing in <head>, with no `defer`.
//
// This strategy does NOT help webpack/ESM apps — their bundles import
// mapbox-gl as a module and never touch window.mapboxgl. The fiber
// walk in watchForMaps() is what catches those.
//
function tryPatchGlobalMapboxgl(): void {
  const w = window as unknown as { mapboxgl?: GlobalMapboxgl };

  // Case A: already fully loaded. Patch in place, nothing more to do.
  if (w.mapboxgl && typeof w.mapboxgl.Map === 'function') {
    patchMapboxgl(w.mapboxgl);
    return;
  }

  // Case B: not there, or there but not yet complete. Install an
  // accessor that fires on assignment. We seed `current` with whatever
  // is on window right now so a partial object (mapboxgl = {} followed
  // later by mapboxgl.Map = ...) isn't lost.
  let current: GlobalMapboxgl | undefined = w.mapboxgl;
  try {
    Object.defineProperty(window, 'mapboxgl', {
      configurable: true,
      enumerable: true,
      get(): GlobalMapboxgl | undefined {
        return current;
      },
      set(next: GlobalMapboxgl | undefined): void {
        current = next;
        if (next && typeof next.Map === 'function') {
          patchMapboxgl(next);
        }
      },
    });
  } catch {
    // Property is non-configurable (rare, e.g. some tools lock it down).
    // We can't intercept — fall through silently and rely on the fiber
    // walk or the explicit registration API.
  }
}

//
// Wrap a mapboxgl object's Map constructor so every new instance is
// (a) registered with us and (b) constructed with preserveDrawingBuffer
// forced on. Safe to call multiple times — the second call notices our
// marker flag and does nothing.
//
function patchMapboxgl(gl: GlobalMapboxgl): void {
  const Original = gl.Map as GlobalMapboxgl['Map'] & { __mve_patched?: boolean };
  if (Original.__mve_patched) return;

  const Patched = function (this: unknown, ...args: unknown[]): MapboxMap {
    // Force preserveDrawingBuffer on. Without it, readPixels and
    // createImageBitmap against the canvas return blank frames after
    // compositing, and our capture loop silently produces black video.
    const [first, ...rest] = args;
    const options =
      first && typeof first === 'object'
        ? { ...(first as Record<string, unknown>), preserveDrawingBuffer: true }
        : first;
    const instance = new Original(options, ...rest);
    registerInstance(instance, 'mapboxgl monkey-patch');
    return instance;
  } as unknown as GlobalMapboxgl['Map'] & { __mve_patched?: boolean };

  // Preserve the prototype chain so `map instanceof mapboxgl.Map`
  // and prototype-level method resolution both still work.
  Patched.prototype = Original.prototype;
  Object.setPrototypeOf(Patched, Original);
  Patched.__mve_patched = true;
  gl.Map = Patched;
}

//
// HTMLCanvasElement.prototype.getContext patch. Every WebGL context
// created after this runs will have preserveDrawingBuffer set to true,
// regardless of what the caller passed. This is how we guarantee
// pixel-readable canvases in webpack/ESM apps where we can't see the
// Map constructor to force the flag there.
//
// Only installed in heavy mode (?mapvideo=1). Idempotent via a marker
// property on the patched function.
//
type GetContextFn = HTMLCanvasElement['getContext'];

function patchGetContextForCapture(): void {
  const proto = HTMLCanvasElement.prototype as unknown as {
    getContext: GetContextFn & { __mve_patched?: boolean };
  };
  const original = proto.getContext;
  if (original.__mve_patched) return;

  const patched = function (
    this: HTMLCanvasElement,
    type: string,
    attrs?: Record<string, unknown>,
  ) {
    if (type === 'webgl' || type === 'webgl2' || type === 'experimental-webgl') {
      // Spread with our override last so we always win over caller intent.
      attrs = { ...(attrs ?? {}), preserveDrawingBuffer: true };
    }
    return (original as (this: HTMLCanvasElement, t: string, a?: unknown) => unknown).call(
      this,
      type,
      attrs,
    );
  } as GetContextFn & { __mve_patched?: boolean };
  patched.__mve_patched = true;
  proto.getContext = patched;
}

//
// Start watching the DOM for Mapbox map containers. Runs three passes:
//
//   - An immediate scan for elements already in the tree (in case our
//     script loaded after the page built its DOM).
//   - A MutationObserver that reacts to any new .mapboxgl-map insertion
//     during the session.
//   - A half-second polling interval for 30 seconds after install. This
//     covers the case where the map DOM lands first but the React ref
//     that holds the Map instance isn't assigned until a few ticks later
//     (React's handleLoad sets map refs after initial render).
//
function watchForMaps(): void {
  const start = (): void => {
    scanAllContainers();
    const observer = new MutationObserver(() => scanAllContainers());
    observer.observe(document.body, { childList: true, subtree: true });

    let retries = 0;
    const interval = window.setInterval(() => {
      scanAllContainers();
      retries++;
      if (retries > 60) window.clearInterval(interval);
    }, 500);
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
}

//
// Look at every .mapboxgl-map element on the page and try to extract
// its Map instance via React fibers. Elements we've already successfully
// walked are skipped as long as we already have at least one instance
// registered — this keeps repeated mutation events cheap.
//
function scanAllContainers(): void {
  const containers = document.querySelectorAll<HTMLElement>('.mapboxgl-map');
  for (const container of containers) {
    if (fiberWalkedContainers.has(container) && instances.size > 0) continue;
    const map = findMapViaReactFiber(container);
    if (map) {
      fiberWalkedContainers.add(container);
      registerInstance(map, 'React fiber walk');
    }
  }
}

//
// Given a .mapboxgl-map DOM element, walk the React fiber tree upward
// from the element's fiber node, inspecting each ancestor for anything
// that duck-types as a Mapbox Map.
//
// React 16+ attaches the fiber to the DOM node under a property whose
// key is `__reactFiber$<random>`. (Older versions used
// __reactInternalInstance$.) From that fiber, .return walks toward the
// root. At each node we inspect props, hooks, and stateNode — which is
// where wrapping components (like react-map-gl or Aquarisk's
// @longline/aqua-ui Map component) typically store the Map ref.
//
function findMapViaReactFiber(element: HTMLElement): MapboxMap | null {
  const fiberKey = Object.keys(element).find(
    (k) => k.startsWith('__reactFiber$') || k.startsWith('__reactInternalInstance$'),
  );
  if (!fiberKey) return null;

  let fiber: FiberNode | null =
    (element as unknown as Record<string, FiberNode>)[fiberKey] ?? null;

  // 200 hops is well beyond any realistic depth from a leaf canvas up
  // to a root context provider; really just a cycle safety net.
  let hops = 0;
  while (fiber && hops < 200) {
    const map = scanFiberNode(fiber);
    if (map) return map;
    fiber = fiber.return ?? null;
    hops++;
  }
  return null;
}

//
// Minimal subset of React's fiber node shape that we actually read.
// React doesn't publish this as a type and the real shape is huge; we
// only care about a handful of fields.
//
interface FiberNode {
  return?: FiberNode | null;
  memoizedProps?: unknown;
  memoizedState?: unknown;
  pendingProps?: unknown;
  stateNode?: unknown;
}

//
// React stores hook state (useState, useRef, useReducer, ...) as a
// singly-linked list of nodes rooted at fiber.memoizedState for function
// components. Each node has a .memoizedState (the hook's value) and a
// .next (the next hook the component called during render).
//
interface HookNode {
  memoizedState?: unknown;
  next?: HookNode | null;
}

//
// Inspect one fiber's props, hooks, and stateNode for anything that
// looks like a Mapbox Map. This is where the actual match happens during
// the upward walk.
//
function scanFiberNode(fiber: FiberNode): MapboxMap | null {
  // Most common path: the Map is held as a prop passed to a child
  // component. E.g. react-map-gl passes the map object down to its
  // children.
  const viaProps = scanValue(fiber.memoizedProps, 0);
  if (viaProps) return viaProps;

  // Newly-scheduled render that hasn't committed yet still has
  // pendingProps populated. Cheap extra check.
  const viaPending = scanValue(fiber.pendingProps, 0);
  if (viaPending) return viaPending;

  // Function components' hooks: follow the linked list and scan each
  // hook's memoizedState. This is how we find useRef-stored maps like
  // Aquarisk's `mapRef.current` inside MapViewBase.
  const hooks = fiber.memoizedState;
  if (looksLikeHook(hooks)) {
    let h: HookNode | null | undefined = hooks;
    let count = 0;
    while (h && count < 60) {
      const m = scanValue(h.memoizedState, 0);
      if (m) return m;
      h = h.next;
      count++;
    }
  } else {
    // Class component state is a plain object on memoizedState; scan it
    // directly.
    const m = scanValue(hooks, 0);
    if (m) return m;
  }

  // stateNode is the class instance (for class components) or the DOM
  // node (for host components). We only inspect it when it's an
  // object that isn't a DOM node, since DOM nodes have no useful
  // ref-to-map relationship.
  const sn = fiber.stateNode;
  if (sn && typeof sn === 'object' && !(sn instanceof Node)) {
    const m = scanValue(sn, 0);
    if (m) return m;
  }

  return null;
}

//
// Duck-typed guard for hook nodes. React's function-component
// memoizedState has both .next and .memoizedState; class components'
// memoizedState is a plain state object without those fields.
//
function looksLikeHook(value: unknown): value is HookNode {
  return (
    typeof value === 'object' &&
    value !== null &&
    'next' in value &&
    'memoizedState' in value
  );
}

//
// Maximum object recursion depth inside scanValue. Needs to be deep
// enough to find a Map nested inside typical React container shapes
// (e.g. provider value → store → map), but shallow enough to avoid
// blowing the call stack on circular props graphs.
//
const MAX_SCAN_DEPTH = 3;

//
// Recursively look for a Map instance inside an arbitrary value.
// Safe against cycles via the depth limit, DOM nodes (skipped), and
// inaccessible properties (try/catch). Checks `.current` refs
// explicitly since that's the most common hiding place.
//
function scanValue(value: unknown, depth: number): MapboxMap | null {
  if (!value || typeof value !== 'object') return null;
  if (depth > MAX_SCAN_DEPTH) return null;
  if (value instanceof Node) return null;

  const direct = tryResolveMap(value);
  if (direct) return direct;

  if (Array.isArray(value)) {
    for (const item of value) {
      const m = scanValue(item, depth + 1);
      if (m) return m;
    }
    return null;
  }

  for (const key of Object.keys(value as Record<string, unknown>)) {
    try {
      const child = (value as Record<string, unknown>)[key];
      if (!child || typeof child !== 'object') continue;
      const resolved = tryResolveMap(child);
      if (resolved) return resolved;
      const m = scanValue(child, depth + 1);
      if (m) return m;
    } catch {
      // Accessing this property threw — typically a getter with side
      // effects on an unrelated object (MobX, Vue reactivity, ...).
      // Skip and keep scanning siblings.
    }
  }
  return null;
}

//
// Collapse any map-like value to a real Mapbox Map, or null.
// Handles three shapes:
//
//   - the Map itself (matches isMapboxMap directly)
//   - a React ref `{ current: Map }` (common pattern in user code)
//   - a react-map-gl MapRef or similar wrapper exposing getMap()
//     which returns the real Map — older versions of react-map-gl
//     (pre-v8) don't proxy every Mapbox method through the ref, so
//     the unwrap is mandatory for anything beyond navigation
//     (setLayoutProperty, addLayer, setStyle, ...).
//
function tryResolveMap(value: unknown): MapboxMap | null {
  if (!value || typeof value !== 'object') return null;
  // Prefer the unwrapped map whenever the candidate has getMap().
  // react-map-gl's MapRef wrapper is the canonical case: it forwards
  // *some* Mapbox methods (enough to pass our duck-type check) but
  // not all — setPixelRatio, for instance, is missing in older
  // versions. Going through getMap() gets us the actual Mapbox Map
  // with its full API surface. Tried before isMapboxMap on purpose
  // so wrappers that coincidentally duck-type still get unwrapped.
  if (hasGetMap(value)) {
    try {
      const inner = value.getMap();
      if (isMapboxMap(inner)) return inner;
    } catch {
      // getMap threw; fall through to checking the candidate
      // itself, in case it IS the map despite looking like a
      // wrapper.
    }
  }
  if (isMapboxMap(value)) return value;
  if (hasCurrentRef(value) && isMapboxMap(value.current)) return value.current;
  return null;
}

//
// Guard for react-map-gl style wrappers that expose the underlying
// Map via a getMap() method.
//
function hasGetMap(value: unknown): value is { getMap: () => unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { getMap?: unknown }).getMap === 'function'
  );
}

//
// True for objects that look like React refs, i.e. `{ current: ... }`.
// We keep it narrow on purpose so it doesn't match arbitrary objects
// that happen to have a `current` field.
//
function hasCurrentRef(value: unknown): value is { current: unknown } {
  return typeof value === 'object' && value !== null && 'current' in value;
}

//
// Duck-type check for a Mapbox Map. We can't use `instanceof` because
// we don't have the Map class reference when the library was imported
// as an ES module. Instead, we require the full set of methods we
// ourselves rely on — getCenter/Zoom/Bearing/Pitch for reading view
// state, jumpTo for setting it, on/once for subscribing to 'idle',
// and getCanvas for pixel capture. Any object that answers all eight
// is, for our purposes, a Map.
//
function isMapboxMap(value: unknown): value is MapboxMap {
  if (!value || typeof value !== 'object') return false;
  const o = value as Record<string, unknown>;
  return (
    typeof o.getCenter === 'function' &&
    typeof o.getZoom === 'function' &&
    typeof o.getBearing === 'function' &&
    typeof o.getPitch === 'function' &&
    typeof o.jumpTo === 'function' &&
    typeof o.once === 'function' &&
    typeof o.on === 'function' &&
    typeof o.getCanvas === 'function' &&
    // setLayoutProperty is load-bearing: it distinguishes the
    // real Mapbox Map from a react-map-gl MapRef wrapper that
    // proxies only the navigation methods. Without this check,
    // we'd register the wrapper and every hideLayers call would
    // silently no-op against it.
    typeof o.setLayoutProperty === 'function'
  );
}
